import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import useMergeTags from '@Hooks/merge-tags/useMergeTags';
import {MergeTagsDropdown} from '@Components/merge-tags/merge-tags-dropdown/merge-tags-dropdown';
import {MergeTagToggler} from '@Components/merge-tags/merge-tag-toggler/merge-tag-toggler';
import type {MergeTag, MergeTagsProps} from './merge-tags.types';
import styles from './merge-tags.module.scss';
import {SIDEBAR_MERGE_TAGS_LIST_ID} from '@Global/email-maker';
import {Text, TextSize} from '@Components/text';
import Divider from '@Components/divider';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';

export function MergeTags({...props}: MergeTagsProps): ReactElement<any> | null {
  const DEFAULT_MERGE_TAG_TOGGLES_TO_SHOW = 3;

  const {mergeTags, addMergeTag, removeMergeTag, defaultValues, updateDefaultValue} = useMergeTags(props.ccid);
  const [visibleMergeTagsToggles, setVisibleMergeTagsToggles] = useState(DEFAULT_MERGE_TAG_TOGGLES_TO_SHOW);
  const isVisibleShowMore = mergeTags.length > DEFAULT_MERGE_TAG_TOGGLES_TO_SHOW;
  const isVisibleShowLess = visibleMergeTagsToggles > DEFAULT_MERGE_TAG_TOGGLES_TO_SHOW;

  useEffect(() => {
    if (!mergeTags.length) {
      return;
    }

    if (isVisibleShowLess) {
      setVisibleMergeTagsToggles(mergeTags.length);
    }
  }, [mergeTags.length]);

  const revealAllMergeTagToggles = (): void => {
    if (isVisibleShowLess) {
      setVisibleMergeTagsToggles(DEFAULT_MERGE_TAG_TOGGLES_TO_SHOW);
    } else {
      setVisibleMergeTagsToggles(mergeTags.length);
    }
  };

  const handleMergeTagSelection = (value: MergeTag['mergeTagKey']): void => {
    addMergeTag(value);
    if (props.onTagAdded) {
      props.onTagAdded(props.ccid, value);
    }
  };

  const handleMergeTagDeletion = (value: MergeTag['mergeTagKey']): void => {
    removeMergeTag(value);
    if (props.onTagRemoved) {
      props.onTagRemoved(props.ccid, value);
    }
  };

  const getShowMoreButton = (): ReactElement<any> | null => {
    if (!isVisibleShowMore) {
      return null;
    }

    return (
      <div className="_full-width flex-row-justify-center">
        <Button
          type={Type.GHOST}
          icon={isVisibleShowLess ? 'icon-caret-up' : 'icon-caret-down'}
          iconPlacement={IconPlacement.RIGHT}
          onClick={revealAllMergeTagToggles}
          customClasses={`${styles.showMoreButton} radius-4`}
          textClasses={styles.showMoreButtonText}
          size={Size.SMALL}
          text={isVisibleShowLess ? window.i18next.t('pmwjs_view_less') : window.i18next.t('pmwjs_view_more')}
        />
      </div>
    );
  };

  const getMergeTagToggles = useMemo((): ReactElement<any>[] => {
    const mergeTagsToRender = [...mergeTags.slice(0, visibleMergeTagsToggles)];
    return mergeTagsToRender.map((tag, index) => {
      const isOpen = index === mergeTags.length - 1; // Only last toggle should be open
      return (
        <MergeTagToggler
          key={tag.mergeTagKey}
          tag={tag}
          onDelete={() => {
            handleMergeTagDeletion(tag.mergeTagKey);
          }}
          onInputChange={(value) => {
            updateDefaultValue(tag.mergeTagKey, value);
            props.onDefaultValueUpdate(props.ccid, tag.mergeTagKey, value);
          }}
          defaultValue={defaultValues[tag.mergeTagKey]}
          placeholder={tag.placeholder}
          isOpenByDefault={isOpen}
        />
      );
    });
  }, [mergeTags, visibleMergeTagsToggles, defaultValues]);

  return (
    <div className="spacing-m-t-6">
      <div className="flexbox">
        <Text val={window.i18next.t('pmwjs_merge_tags')} className="content-body" bold size={TextSize.XSMALL} />
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.TEXT,
            text: window.i18next.t('pmwjs_new'),
            isBold: true,
            textColor: 'content-body-white',
          }}
          background={PILL_BACKGROUND_COLOR.SECONDARY_100}
          size={PILL_SIZE.XSMALL}
          className="spacing-p-l-2 spacing-p-r-2 spacing-m-l-2"
        />
      </div>
      <Divider additionalClasses="spacing-m-t-1 spacing-m-b-3" />
      <MergeTagsDropdown
        listId={SIDEBAR_MERGE_TAGS_LIST_ID}
        onSelect={handleMergeTagSelection}
        selector={
          <Button
            icon="icon-plus"
            isFullWidth
            type={Type.DASHED}
            iconClassName="size-icon-24"
            customClasses={`spacing-m-b-4 radius-12 _overflow-hidden ${styles.mergeTagsAddButton}`}
          />
        }
        popupOffset={-4}
        leftOffset={0}
        popupClassName={styles.popup}
      />
      <div>
        <div>{getMergeTagToggles}</div>
        {getShowMoreButton()}
      </div>
    </div>
  );
}

export default React.memo(MergeTags);
